import React, {useRef, useState} from 'react';

import {User} from 'constants/UserConstants';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import EditableComponent from 'notebook/mediocre/core-ui/EditableComponent';
import {SelectionPartition} from 'notebook/mediocre/EditorTypes';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {BenchlingSelection, SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import * as DateUtil from 'util/DateUtil';
import {ElementClipboardEvent, ElementKeyboardEvent} from 'util/Types';

const MAX_TITLE_LENGTH = 255;
const TITLE_CONTENT_EDITABLE_INDEX = 0;

export const TitleEditorCID = 'notetitleeditor';

interface TitleEditorProps {
  title: string;
  badge?: React.ReactNode;
  humanId?: string;
  templateVersionId?: number;
  templateUrl?: string;
  templateVersionTimestamp?: string;
  creator: User;
  moveHandler: ({up, beginning}: {up: boolean; beginning: boolean}) => void;
  storeState: (selectionData: {selection: SelectionRestoreData}) => void;
  restoreState: (e: ElementKeyboardEvent, selectionRestoreData: {selection: SelectionRestoreData}) => boolean;
  updateTitle?: (title: string) => void;
  requestSelection: (args: {selection: BenchlingSelection}) => void;
  locked: boolean;
  spellCheck?: boolean;
  editorIcon?: JSX.Element;
}

function TitleEditor(props: TitleEditorProps): JSX.Element {
  const [originalSelection, setOriginalSelection] = useState<BenchlingSelection | null>(null);
  const contentEditable = useRef<React.ComponentRef<typeof EditableComponent> | null>(null);

  function updateHandler(evt: {
    text: string;
    formats: Format;
    target: Element | EventTarget;
    selection?: BenchlingSelection;
  }): void {
    const {text} = evt;
    if (text.length <= MAX_TITLE_LENGTH && props.updateTitle) {
      props.updateTitle(text);
      props.requestSelection({selection: selutil.getSelectionIndices()!});
    } else if (originalSelection != null) {
      // forceContentEditableUpdate to reset component to show original text
      contentEditable.current?.forceContentEditableUpdate();
      props.requestSelection({selection: originalSelection});
    }
  }

  function pasteHandler(
    e: SelectionPartition & {
      originalEvent: ElementClipboardEvent;
    }
  ): void {
    if (props.locked || !props.updateTitle) {
      return;
    }

    const {before, after, originalEvent} = e;
    const parts = wyciwyp.processClipboard(originalEvent.clipboardData, {});
    const allowedPasteLength = MAX_TITLE_LENGTH - before.length - after.length;
    const pasteText = parts
      .map((part) => part.text)
      .join(' ')
      .slice(0, allowedPasteLength);
    const text = before + pasteText + after;
    const pos = text.length - after.length;

    props.updateTitle(text);
    props.requestSelection({selection: {cid: TitleEditorCID, start: pos, end: pos}});
  }

  function keyDownHook(): void {
    setOriginalSelection(selutil.getSelectionIndices());
  }

  function readableTime(): string {
    const options = {showYear: true, showTime: true};
    const date = new Date(props.templateVersionTimestamp ?? '');
    return DateUtil.readableTime(date, options);
  }

  return (
    // Note: when exporting a PDF with the new Review History UI we are repeating the Entry title twice.
    // Once at the beginning of the metadata section and once before the Entry contents.
    // We are currently implementing this by adding some inline HTML at the metadata section.
    // (see EntryExportMetadataWithEsignatures in in EntryExportMetadata.tsx)
    // Therefore, any changes here that stylistically update the way a title looks when exported
    // should ALSO BE REPLICATED in `EntryExportMetadataWithEsignatures` to ensure consistency.
    // There is a TODO to fix that by reusing the same component BNCH-130428.
    <h1 className="mediocre-titleEditor" {...getTestProps({component: 'TitleEditor', element: 'root'})}>
      <span className="mediocre-titleEditor-header">
        {/* Always render a <span/> even if humanId is not specified so flexbox alignment is the same. */}
        {/* Add a space at the end to make the ID precisely selectable using double-click. */}
        <div className="mediocre-titleEditor-header-group">
          {props.templateUrl ? (
            <div>
              <div
                id="human-id"
                className="mediocre-titleEditor-header-id u-faded"
                {...getTestProps({component: 'TitleEditor', element: 'humanId'})}
              >
                {props.humanId}
              </div>
              <div id="template-link" className="mediocre-titleEditor-header-id u-faded">
                <Tooltip
                  text={
                    <>
                      <b>Timestamp</b>
                      <br />
                      {readableTime()}
                    </>
                  }
                >
                  <a
                    href={
                      props.templateVersionId
                        ? `${props.templateUrl}?versionId=${props.templateVersionId}`
                        : `${props.templateUrl}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    {...getTestProps({component: 'TitleEditor', element: 'sourceTemplateLink'})}
                  >
                    Source template
                  </a>
                </Tooltip>
              </div>
            </div>
          ) : (
            <div>
              <span
                id="human-id"
                className="mediocre-titleEditor-header-id u-faded"
                {...getTestProps({component: 'TitleEditor', element: 'humanId'})}
              >
                {props.humanId}&nbsp;
              </span>
            </div>
          )}
          <div id="badge-button" {...getTestProps({component: 'TitleEditor', element: 'badge'})}>
            {props.badge}
          </div>
        </div>
      </span>
      <div
        className="mediocre-titleEditor-icon-editable-wrapper"
        {...getTestProps({component: 'TitleEditor', element: 'editable'})}
      >
        {props.editorIcon && <div className="mediocre-titleEditor-icon">{props.editorIcon}</div>}
        <EditableComponent
          ref={contentEditable}
          allowRichText={false}
          allowMultiline={false}
          cid={TitleEditorCID}
          className="mediocre-titleEditor-titleEditable"
          text={props.title}
          formats={null}
          index={TITLE_CONTENT_EDITABLE_INDEX}
          moveHandler={props.moveHandler}
          updateHandler={updateHandler}
          pasteHandler={pasteHandler}
          storeState={props.storeState}
          restoreState={props.restoreState}
          keyDownHook={keyDownHook}
          locked={props.locked || props.updateTitle == null}
          spellCheck={props.spellCheck ?? false}
        />
      </div>
    </h1>
  );
}

export default React.memo(TitleEditor);
